import { useQuery } from "@tanstack/react-query";
import { invalidateCoverageBoard } from "@/react/features/coverage/api/coverage.api";
import { invalidateSurfaceCaches } from "@/react/features/promoteSurface/api/promoteSurface.api";
import { rejectSurfaceAsset } from "@/react/features/promoteSurface/api/promoteSurface.service";
import { invalidateMarketingAssets } from "./marketingAssets.cache";
import {
  marketingAssetKeys,
  type MarketingAssetEntityType,
} from "./marketingAssets.query-keys";
import { getAllMarketingAssetsForEntity } from "./marketingAssets.service";
import type { SwivuMarketingAsset } from "./marketingAssets.types";

export {
  DEMOTE_ASSET_CONFIRM_DESCRIPTION,
  DEMOTE_ASSET_LEAVE_COVERAGE_NOTE,
} from "./marketingAssets.constants";

export { invalidateMarketingAssets } from "./marketingAssets.cache";

export interface UseEntityMarketingAssetsParams {
  boardId: number;
  entityType: MarketingAssetEntityType;
  entityId: number;
  enabled?: boolean;
}

export interface UseEntityMarketingAssetsResult {
  assets: SwivuMarketingAsset[];
  isLoading: boolean;
}

/**
 * Entity-scoped marketing asset list from
 * GET /promote/board/{board_id}/marketing-assets.
 *
 * Loads all pages (API max 100/page) so OrganizeStep / editor see the full set.
 */
export function useEntityMarketingAssets({
  boardId,
  entityType,
  entityId,
  enabled = true,
}: UseEntityMarketingAssetsParams): UseEntityMarketingAssetsResult {
  const result = useQuery({
    queryKey: marketingAssetKeys.entity(boardId, entityType, entityId),
    queryFn: () =>
      getAllMarketingAssetsForEntity({ boardId, entityType, entityId }),
    enabled: enabled && !!boardId && Number.isFinite(entityId),
    staleTime: 30_000,
  });

  return {
    assets: result.data?.results ?? [],
    isLoading: result.isLoading,
  };
}

/**
 * Demote via Surface reject, then invalidate marketing tile, Coverage, and Surface.
 */
export async function demoteAcceptedMarketingAsset(params: {
  slug: string;
  assetId: number;
  boardId: number;
  entityType?: MarketingAssetEntityType;
  entityId?: number;
}): Promise<void> {
  const { slug, assetId, boardId, entityType, entityId } = params;

  await rejectSurfaceAsset(slug, assetId);

  await Promise.all([
    invalidateMarketingAssets(boardId, entityType, entityId),
    invalidateCoverageBoard(slug),
    invalidateSurfaceCaches(slug),
  ]);
}
